import { arrow, computePosition, flip, offset, shift } from '@floating-ui/react-dom';
import clsx from 'clsx';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

interface PopoverProps {
  baseElement?: HTMLElement | null;
  className?: string;
  error?: string;
  warning?: string;
  info?: ReactNode;
}

/**
 * The popover will be displayed when the base element contains the CSS class `group/cell` and has focus.
 */
export function Popover({
  baseElement,
  className,
  error,
  warning,
  info,
}: Readonly<PopoverProps>): ReactElement<PopoverProps> {
  const [popoverRef, setPopoverRef] = useState<HTMLDivElement | null>();
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>();

  const handleWindowSizeChange = () => {
    if (baseElement && popoverRef && arrowRef)
      computePosition(baseElement, popoverRef, {
        placement: 'top',
        middleware: [offset(16), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
      })
        .then(({ x, y, placement, middlewareData }) => {
          Object.assign(popoverRef.style, {
            left: `${x}px`,
            top: `${y}px`,
          });

          const { x: arrowX, y: arrowY } = middlewareData.arrow ?? { x: 0, y: 0 };

          const staticSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[placement.split('-')[0]]!;

          Object.assign(arrowRef.style, {
            left: arrowX != null ? `${arrowX}px` : '', // != instead of !== is important, because we need
            top: arrowY != null ? `${arrowY}px` : '', // to check for null and undefined at the same time
            right: '',
            bottom: '',
            [staticSide]: '-8px',
          });
        })
        .catch((error: unknown) => console.error(error));
  };

  handleWindowSizeChange();

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => window.removeEventListener('resize', handleWindowSizeChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseElement, error, warning, info]);

  const coloring = [
    { 'bg-error-500': error },
    { 'bg-warning': warning && !error },
    { 'bg-grey-400': info && !warning && !error },
  ];

  return (
    <div
      ref={setPopoverRef}
      className={clsx(
        'pointer-events-none absolute left-0 top-0 z-40 flex w-max flex-col items-center justify-center opacity-0 drop-shadow-md transition-opacity group-focus-within/cell:opacity-100',
        className,
      )}
    >
      <div
        role="alert"
        className={clsx('z-10 max-w-xs rounded-md px-2 py-1 text-sm font-semibold text-white', coloring)}
      >
        {error ?? warning ?? info}
      </div>
      <div ref={setArrowRef} className={clsx('absolute aspect-square w-4 rotate-45', coloring)} />
    </div>
  );
}
